import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

interface Props {
    onBack: () => void;
    title: React.ReactNode;
    subtitle?: React.ReactNode;
}

/**
 * ArticlePageHeader — gradient hero bar for the article create/edit screens.
 *
 * Replaces the generic MikaPageHeader on this module with the design's branded
 * gradient bar: navy → mid-navy → cobalt, 14px radius, soft drop shadow.
 * The circular back button matches the navy treatment used elsewhere in the
 * redesign (rgba white on the dark surface).
 *
 * Title is white 20/700, ellipsis on overflow. Optional subtitle shows below
 * the title in a softer ink tone.
 */
const ArticlePageHeader: React.FC<Props> = ({ onBack, title, subtitle }) => {
    const intl = useIntl();
    // Imperative use for aria-label — screen readers announce the localized
    // "Kthehu" / "Back" / "Zurück" instead of the icon.
    const backLabel = intl.formatMessage({ id: 'admin.articles.header.back', defaultMessage: 'Kthehu' });

    return (
        <header className="art-page-header" role="banner">
            <button
                type="button"
                className="art-page-header__back"
                onClick={onBack}
                aria-label={backLabel}
            >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polyline points="15 18 9 12 15 6" />
                </svg>
                <span className="sr-only">
                    <FormattedMessage id="admin.articles.header.back" defaultMessage="Kthehu" />
                </span>
            </button>
            <div className="art-page-header__text">
                <h1 className="art-page-header__title">{title}</h1>
                {subtitle && <p className="art-page-header__subtitle">{subtitle}</p>}
            </div>
        </header>
    );
};

export default ArticlePageHeader;
